<script>
import gmapStyles from '@root/public/assets/gmap/gmapStyles'
import { isNonEmptyArray } from '@/helpers/array-helpers'
import OverviewStatsDto from '@/models/overview/overviewStatsDto'
import ClientGroupOverviewFillRateChart from './client-group-overview-fill-rate-chart.vue'
export default {
  name: 'ClientGroupOverviewSchoolsMap',
  components: { ClientGroupOverviewFillRateChart },
  props: {
    schools: {
      type: Array,
      default: () => [],
    },
    focusOnSchool: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      center: { lat: 10, lng: 10 },
      mapTypeId: 'roadmap',
      infoWindow: {
        position: { lat: 0, lng: 0 },
        open: false,
        location: {},
        overviewStats: new OverviewStatsDto(),
      },
      mapOptions: {
        disableDefaultUI: true,
        styles: gmapStyles(),
      },
    }
  },
  computed: {
    schoolsWithAddressData() {
      if (!this.hasSchools) return []
      return this.schools.filter(
        (school) =>
          typeof school.address.latitude !== 'undefined' &&
          typeof school.address.longitude !== 'undefined'
      )
    },
    hasSchools() {
      return isNonEmptyArray(this.schools)
    },
    compInfoWindowOpenBookings() {
      return this.infoWindow?.overviewStats?.unfilled || 0
    },
    compInfoWindowTotalBookings() {
      return this.infoWindow?.overviewStats?.totalBookings || 0
    },
    compInfoWindowLocationName() {
      return this.infoWindow?.location?.displayName || ''
    },
    compInfoWindowLocationAddress() {
      return this.infoWindow?.location?.address?.fullAddress || ''
    },
  },
  watch: {
    schools: {
      handler() {
        this.setCenter()
      },
    },
    focusOnSchool: {
      handler() {
        if (!this.focusOnSchool || !this.hasSchools) return
        const schoolFound = this.schools.find(
          (school) => school.locationId === this.focusOnSchool
        )

        if (schoolFound) {
          this.setCenter(schoolFound)
          this.infoWindow.open = false // Closes any already open info windows
          this.openInfoWindowTemplate(schoolFound)
        }
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setCenter()
    })
  },
  methods: {
    openInfoWindowTemplate(location) {
      this.infoWindow.location = location
      const position = {
        lat: this.infoWindow.location.address.latitude,
        lng: this.infoWindow.location.address.longitude,
      }
      // Centre map on marker
      this.center = position
      // Generate InfoWindow
      this.infoWindow.position = position
      // Open InfoWindow
      this.infoWindow.open = true

      this.infoWindow.overviewStats = new OverviewStatsDto({
        openBookings: location.openBookings,
        totalBookings: location.totalBookings,
      })
    },
    setCenter(school) {
      if (!this.hasSchools) return

      const schoolToUseAsCenter = school || this.schools[0]

      this.center = {
        lat: schoolToUseAsCenter.address.latitude,
        lng: schoolToUseAsCenter.address.longitude,
      }
    },
    navigateToLocation(locationId) {
      return this.$emit('navigateToLocation', locationId)
    },
    navigateToRegion(regionId) {
      return this.$emit('navigateToRegion', regionId)
    },
    getPosition(school) {
      return {
        lat: school.address.latitude,
        lng: school.address.longitude,
      }
    },
  },
}
</script>

<template>
  <v-sheet class="rounded-xl cl-elevation">
    <GmapMap
      ref="mapRef"
      :center="center"
      :zoom="11"
      class="overview-map"
      map-style-id="roadmap"
      :options="mapOptions"
    >
      <GmapMarker
        v-for="(school, index) in schoolsWithAddressData"
        :key="index"
        :position="getPosition(school)"
        @click="openInfoWindowTemplate(school)"
      />
      <gmap-info-window
        :options="{
          width: 240,
          pixelOffset: { width: 0, height: -35 },
        }"
        :position="infoWindow.position"
        :opened="infoWindow.open"
        @closeclick="infoWindow.open = false"
      >
        <v-sheet v-if="infoWindow">
          <header class="mb-2" style="width: 240px">
            <h6 class="text-subtitle-2">
              {{ compInfoWindowLocationName }}
            </h6>
            <span class="text-caption">{{
              compInfoWindowLocationAddress
            }}</span>
          </header>
          <v-divider class="mb-2" />
          <div class="d-flex align-center justify-space-between">
            <div class="text-center">
              <p class="text-caption mb-1">Total</p>
              <data class="text-h6">
                {{ compInfoWindowTotalBookings }}
              </data>
            </div>

            <v-divider vertical style="height: 100%" />

            <div class="text-center mr-4">
              <p class="text-caption mb-1">Open</p>
              <data class="text-h6">{{ compInfoWindowOpenBookings }}</data>
            </div>

            <ClientGroupOverviewFillRateChart
              :overview-stats="infoWindow.overviewStats"
              :width="70"
              :height="70"
              :cut-out="25"
              hide-subtitle
              text-size="14px"
            />
          </div>
        </v-sheet>
      </gmap-info-window>
    </GmapMap>
  </v-sheet>
</template>

<style scoped>
.vue-map-container {
  width: 100%;
  height: 450px;
}

.overview-map {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
